import React, { useState } from "react";
import {
  Grid,
  Button,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import { NavigateBefore, NavigateNext, Send } from "@mui/icons-material";
import { usePaymentReminderConfig } from "../context/PaymentReminderConfig.context";
import { useBillingCompanyContext } from "../../../../providers";
import { WhatsAppTemplatePicker } from "./WhatsAppTemplatePicker";
import {
  CustomTextField,
  ControlledCustomSelect,
  CountryCodeSelect,
} from "../../../../components/formsComponents";
import { FileUpload } from "../../../../components/FileUpload/components/FileUpload";
import { PreviewTemplate } from "../../../../components/Preview/PreviewTemplate";
import { IWhatsAppTemplateData } from "../../../../redux/whatsappTemplate/whatsappTemplate.types";
import WhatsappBgImg from "../../../../assets/whatsapp_bg_img.jpg";
import { formatText } from "../../../../helpers";
import { api } from "../../../../api/api";
import { useDispatchWrapper } from "../../../../hooks";
import { showMessage } from "../../../../redux/messages/messagesActions";
import { uploadFile } from "../../../../components/FileUpload/utils";
import { config } from "../../../../config";

interface IStep2TemplateAndVariablesProps {
  /** Hide the "Test Recipients" section — not needed in some flows */
  showTestRecipients?: boolean;
}

/**
 * Header media is modeled as one unit because "newly picked file" and
 * "already-saved url" are mutually exclusive — they must always change together.
 */
interface IHeaderMediaState {
  type: string; // "" | "IMAGE" | "VIDEO" | "DOCUMENT"
  file: File | null; // newly picked file (not yet uploaded)
  existingUrl: string; // saved S3 url restored in edit mode
}

export const Step2TemplateAndVariables: React.FC<IStep2TemplateAndVariablesProps> = ({
  showTestRecipients = false,
}) => {
  const { formState, updateFormState, goNext, goPrev } =
    usePaymentReminderConfig();
  const { comapny } = useBillingCompanyContext();

  const [selectedTemplate, setSelectedTemplate] =
    useState<IWhatsAppTemplateData | null>(null);
  const [moduleVariables, setModuleVariables] = useState<
    { type: string; value: string }[]
  >(formState.module_variables_columns_name || []);
  const [buttonVariableType, setButtonVariableType] = useState<string>("SELECT");
  const [buttonVariableValue, setButtonVariableValue] = useState<string>("");
  // Consolidated header media state — keeps type/newFile/existingUrl in sync as one unit
  // so "new file picked" and "existing saved url" can never drift out of sync with each other.
  const [headerMedia, setHeaderMedia] = useState<IHeaderMediaState>({
    type: "",
    file: null,
    existingUrl: formState.header_media?.[0]?.value || "",
  });
  const [showErrors, setShowErrors] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(!!formState.template_name);

  // Test Recipients
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [testCountryCode, setTestCountryCode] = useState("+91");
  const [testSending, setTestSending] = useState(false);

  const dispatch = useDispatchWrapper();

  // Get mapping_keys from context (set by Step1 from rule template)
  const mappingKeys = formState.mapping_keys || {};
  const mappingKeyOptions = Object.keys(mappingKeys)
    .filter((key) => !/(^id$|_id$|uuid|_uuid)/i.test(key))
    .map((key) => ({
      label: formatText(key),
      value: mappingKeys[key],
    }));

  // If template already selected (edit mode), fetch it on mount
  React.useEffect(() => {
    if (formState.template_name && !selectedTemplate) {
      setTemplateLoading(true);
      const fetchSelectedTemplate = async () => {
        try {
          const { api } = await import("../../../../api/api");
          let url = `/template/get-whatsapp-template?pageNo=1&pageLimit=1&itemPerPage=1&status=APPROVED`;
          url += `&company_outlet_uuid=${comapny.company_outlet_uuid}`;
          url += `&columns=${JSON.stringify(["name"])}&value=${formState.template_name}`;
          const res = await api.get(url);
          const templates = res.data.data || [];
          const match = templates.find((t: any) => t.name === formState.template_name);
          if (match) {
            const components = match.components || [];
            const normalized: IWhatsAppTemplateData = {
              ...match,
              name: match.name || "",
              category: match.category || "",
              example: { header_text: [], body_text: [] },
              company_outlet_uuid: match.company_outlet_uuid || "",
              meta_id: match.meta_id || null,
              template_id: match.template_id || undefined,
              header: components.find((c: any) => c.type === "HEADER") || { type: "HEADER", text: "", format: null, example: { header_text: [[]], header_handle: null } },
              body: components.find((c: any) => c.type === "BODY") || { type: "BODY", text: "", example: { body_text: [[]] } },
              footer: components.find((c: any) => c.type === "FOOTER") || { type: "FOOTER", text: "" },
              buttons: components.find((c: any) => c.type === "BUTTONS") || { type: "BUTTONS", buttons: [] },
            } as IWhatsAppTemplateData;
            setSelectedTemplate(normalized);

            // Restore header type + already-saved media URL together (edit mode)
            if (normalized.header?.format && ["IMAGE", "VIDEO", "DOCUMENT"].includes(normalized.header.format)) {
              setHeaderMedia({
                type: normalized.header.format,
                file: null,
                existingUrl: formState.header_media?.[0]?.value || "",
              });
            }

            // Initialize button variable from saved data
            if (formState.button_variable_column_name?.length > 0) {
              setButtonVariableType(formState.button_variable_column_name[0].type || "SELECT");
              setButtonVariableValue(formState.button_variable_column_name[0].value || "");
            }
          }
        } catch {
          // fallback to picker
        } finally {
          setTemplateLoading(false);
        }
      };
      fetchSelectedTemplate();
    }
  }, []);

  // Helpers
  const getBodyVariableCount = (template: IWhatsAppTemplateData | null) => {
    if (!template?.body?.text) return 0;
    const matches = template.body.text.match(/\{\{[^}]+\}\}/g);
    return matches ? matches.length : 0;
  };

  const hasButtonVariable = (template: IWhatsAppTemplateData | null): boolean => {
    if (!template?.buttons?.buttons?.length) return false;
    // Show button variables if any button is URL type (with or without example)
    return template.buttons.buttons.some(
      (btn: any) => btn?.type?.toLowerCase() === "url",
    );
  };

  const hasHeaderMedia = (template: IWhatsAppTemplateData | null): boolean => {
    if (!template?.header?.format) return false;
    return ["IMAGE", "VIDEO", "DOCUMENT"].includes(template.header.format);
  };

  const handleTemplateSelect = (template: IWhatsAppTemplateData) => {
    setSelectedTemplate(template);
    setShowErrors(false);

    // Initialize body variables
    const bodyVarCount = getBodyVariableCount(template);
    const newModuleVars = Array.from({ length: bodyVarCount }, () => ({
      type: "SELECT",
      value: "",
    }));
    setModuleVariables(newModuleVars);

    // Header — switching templates always resets any previously picked/saved media
    if (template.header?.format && ["IMAGE", "VIDEO", "DOCUMENT"].includes(template.header.format)) {
      setHeaderMedia({ type: template.header.format, file: null, existingUrl: "" });
    } else {
      setHeaderMedia({ type: "", file: null, existingUrl: "" });
    }

    // Button variable
    if (hasButtonVariable(template)) {
      setButtonVariableType("SELECT");
      setButtonVariableValue("");
    }

    updateFormState({
      whatsapp_template_uuid: template.whatsapp_template_uuid || template.name,
      template_name: template.name,
    });
  };

  const handleVariableChange = (index: number, field: "type" | "value", value: string) => {
    const updated = [...moduleVariables];
    if (field === "type") {
      updated[index] = { type: value, value: "" };
    } else {
      updated[index] = { ...updated[index], value };
    }
    setModuleVariables(updated);
  };

  const handleNext = async () => {
    // Validate
    const hasUnfilledBodyVars = moduleVariables.some((v) => !v.value || v.value.trim() === "");
    const hasUnfilledButton = hasButtonVariable(selectedTemplate) && (!buttonVariableValue || buttonVariableValue.trim() === "");

    if (hasUnfilledBodyVars || hasUnfilledButton) {
      setShowErrors(true);
      return;
    }

    // Upload header media if a new file was picked, otherwise keep the existing saved URL
    let headerMediaValue = headerMedia.existingUrl || "";
    if (headerMedia.file && headerMedia.type) {
      try {
        const path = await uploadFile({
          file: headerMedia.file,
          module_name: "WHATSAPP_MARKET_CAMPAIGN",
          defaultReturn: "",
          asPayload: {},
          onWarning: (message) => {
            dispatch(
              showMessage({
                type: "warning",
                message,
                displayAs: "snackbar",
              }),
            );
          },
        });
        headerMediaValue = `${config.assets.s3BaseUrl}/` + path;
      } catch {
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to upload header media",
            displayAs: "snackbar",
          }),
        );
        return;
      }
    }

    const button_variable_column_name = hasButtonVariable(selectedTemplate)
      ? [{
          type: buttonVariableType,
          value: buttonVariableValue,
          sub_type: selectedTemplate?.buttons?.buttons?.[0]?.type?.toLowerCase() || "url",
        }]
      : [];

    updateFormState({
      module_variables_columns_name: moduleVariables,
      button_variable_column_name,
      header_media: headerMedia.type
        ? [{ type: headerMedia.type, value: headerMediaValue }]
        : [],
    });
    goNext();
  };

  const bodyVarCount = getBodyVariableCount(selectedTemplate);

  // Build body variable values map for live preview
  const bodyVariableValues: { [key: string]: string } = {};
  moduleVariables.forEach((v, i) => {
    if (v.type === "SELECT" && v.value) {
      bodyVariableValues[`${i + 1}`] = `{{${v.value}}}`;
    } else if (v.value) {
      bodyVariableValues[`${i + 1}`] = v.value;
    } else {
      bodyVariableValues[`${i + 1}`] = `{{${i + 1}}}`;
    }
  });

  return (
    <Grid container spacing={2}>
      {/* Loading pre-selected template */}
      {templateLoading && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Loading template details...
            </Typography>
          </Box>
        </Grid>
      )}

      {/* Template Selection — shown only when no template selected and not loading */}
      {!selectedTemplate && !templateLoading && (
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Select WhatsApp Template
          </Typography>
          <WhatsAppTemplatePicker
            companyOutletUuid={comapny.company_outlet_uuid}
            selectedTemplateUuid={formState.whatsapp_template_uuid}
            onTemplateSelect={handleTemplateSelect}
          />
        </Grid>
      )}

      {/* After Selection — Left: Variables | Right: Preview */}
      {selectedTemplate && !templateLoading && (
        <>
          {/* Left — Variable Mapping */}
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {formState.template_name}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setSelectedTemplate(null)}>
                Change
              </Button>
            </Stack>

            {/* Header Section */}
            {hasHeaderMedia(selectedTemplate) && (
              <Box mb={2}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                  Header
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "primary.main" }}>
                      <TableRow>
                        <TableCell sx={{ color: "#fff", width: 40 }}>#</TableCell>
                        <TableCell sx={{ color: "#fff", width: 120 }}>Type</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>1</TableCell>
                        <TableCell>
                          <ControlledCustomSelect
                            fullWidth
                            value="UPLOAD"
                            options={[{ label: "Upload", value: "UPLOAD" }]}
                            onChange={() => {}}
                          />
                        </TableCell>
                        <TableCell>
                          <FileUpload
                            accept={
                              headerMedia.type === "IMAGE" ? "image/*"
                                : headerMedia.type === "VIDEO" ? "video/*"
                                : headerMedia.type === "DOCUMENT" ? ".pdf"
                                : ""
                            }
                            onChange={(file: File) =>
                              setHeaderMedia((prev) => ({ ...prev, file, existingUrl: "" }))
                            }
                            onDelete={() =>
                              setHeaderMedia((prev) => ({ ...prev, file: null, existingUrl: "" }))
                            }
                            value={headerMedia.file?.name || headerMedia.existingUrl || ""}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Body Variables Table */}
            {bodyVarCount > 0 && (
              <Box mb={2}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                  Body Variables
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "primary.main" }}>
                      <TableRow>
                        <TableCell sx={{ color: "#fff", width: 40 }}>#</TableCell>
                        <TableCell sx={{ color: "#fff", width: 120 }}>Type</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {moduleVariables.map((variable, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <ControlledCustomSelect
                              fullWidth
                              value={variable.type}
                              options={[
                                { label: "Text", value: "TEXT" },
                                { label: "Select", value: "SELECT" },
                              ]}
                              onChange={(e) => handleVariableChange(index, "type", e.target.value as string)}
                            />
                          </TableCell>
                          <TableCell>
                            {variable.type === "TEXT" ? (
                              <CustomTextField
                                fullWidth
                                value={variable.value}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  handleVariableChange(index, "value", e.target.value)
                                }
                                placeholder="Enter value"
                                error={showErrors && !variable.value.trim()}
                                helperText={showErrors && !variable.value.trim() ? "Required" : ""}
                              />
                            ) : (
                              <ControlledCustomSelect
                                fullWidth
                                value={variable.value}
                                options={mappingKeyOptions}
                                onChange={(e) => handleVariableChange(index, "value", e.target.value as string)}
                                displayEmpty
                                placeholder="Select column"
                                error={showErrors && !variable.value}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Button Variables Table */}
            {hasButtonVariable(selectedTemplate) && (
              <Box mb={2}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                  Button Variables
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "primary.main" }}>
                      <TableRow>
                        <TableCell sx={{ color: "#fff", width: 40 }}>#</TableCell>
                        <TableCell sx={{ color: "#fff", width: 120 }}>Type</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>1</TableCell>
                        <TableCell>
                          <ControlledCustomSelect
                            fullWidth
                            value={buttonVariableType}
                            options={[
                              { label: "Text", value: "TEXT" },
                              { label: "Select", value: "SELECT" },
                            ]}
                            onChange={(e) => {
                              setButtonVariableType(e.target.value as string);
                              setButtonVariableValue("");
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {buttonVariableType === "TEXT" ? (
                            <CustomTextField
                              fullWidth
                              value={buttonVariableValue}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setButtonVariableValue(e.target.value)
                              }
                              label={selectedTemplate?.buttons?.buttons?.[0]?.url || ""}
                              placeholder={`e.g. ${selectedTemplate?.buttons?.buttons?.[0]?.example?.[0] || ""}`}
                              error={showErrors && !buttonVariableValue.trim()}
                              helperText={
                                showErrors && !buttonVariableValue.trim()
                                  ? "Required"
                                  : selectedTemplate?.buttons?.buttons?.[0]?.url
                                    ? `URL: ${selectedTemplate.buttons.buttons[0].url}`
                                    : ""
                              }
                            />
                          ) : (
                            <ControlledCustomSelect
                              fullWidth
                              value={buttonVariableValue}
                              options={mappingKeyOptions}
                              onChange={(e) => setButtonVariableValue(e.target.value as string)}
                              displayEmpty
                              placeholder="Select column"
                              error={showErrors && !buttonVariableValue}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* No variables message */}
            {bodyVarCount === 0 && !hasButtonVariable(selectedTemplate) && !hasHeaderMedia(selectedTemplate) && (
              <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  This template has no variables to fill. You can proceed to the next step.
                </Typography>
              </Box>
            )}

            {/* Test Recipients Section */}
            {showTestRecipients && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Test Recipients (optional)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <CountryCodeSelect
                      value={testCountryCode}
                      onChange={(value) => setTestCountryCode(value)}
                    />
                    <CustomTextField
                      placeholder="Enter phone number"
                      value={testPhoneNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && val.length <= 10) {
                          setTestPhoneNumber(val);
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Send />}
                      disabled={testPhoneNumber.length !== 10 || testSending}
                      onClick={async () => {
                        setTestSending(true);
                        try {
                          const bodyVar = moduleVariables.map((v) => ({
                            type: "text",
                            value: v.value || "TEST_VALUE",
                          }));

                          const buttonVar = hasButtonVariable(selectedTemplate)
                            ? [{
                                sub_type: selectedTemplate?.buttons?.buttons?.[0]?.type?.toLowerCase() || "url",
                                type: "BUTTON",
                                payload_type: buttonVariableType.toLowerCase(),
                                value: buttonVariableValue || selectedTemplate?.buttons?.buttons?.[0]?.example?.[0] || "",
                              }]
                            : [];

                          const payload = {
                            fromNumber: null,
                            toNumber: testCountryCode + testPhoneNumber,
                            is_automatic_message: 0,
                            templateName: selectedTemplate?.name,
                            company_outlet_uuid: comapny.company_outlet_uuid,
                            company_outlet_name: comapny.company_outlet_name,
                            objectVariables: {
                              ...(bodyVar.length > 0 && { bodyVar }),
                              ...(buttonVar.length > 0 && { buttonVar }),
                            },
                            whatsapp_web_service: false,
                          };

                          await api.post("/activity/upsert-whatsapp-activity", payload);
                          dispatch(
                            showMessage({
                              type: "success",
                              message: "Test message sent successfully",
                              displayAs: "snackbar",
                            }),
                          );
                        } catch (err: any) {
                          dispatch(
                            showMessage({
                              type: "error",
                              message: err.response?.data?.message || "Failed to send test message",
                              displayAs: "snackbar",
                            }),
                          );
                        } finally {
                          setTestSending(false);
                        }
                      }}
                    >
                      {testSending ? "Sending..." : "Test Template"}
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Grid>

          {/* Right — Template Preview */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Preview
            </Typography>
            <Box
              sx={{
                backgroundColor: "#efeae2",
                backgroundImage: `url(${WhatsappBgImg})`,
                backgroundRepeat: "repeat",
                backgroundSize: "auto",
                backgroundBlendMode: "multiply",
                borderRadius: 2,
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                minHeight: 300,
              }}
            >
              <PreviewTemplate
                template={selectedTemplate}
                bgImage={false}
                headerMedia={headerMedia.file}
                headerType={headerMedia.type}
                headerVariableValue={headerMedia.existingUrl}
                bodyVariableValues={bodyVariableValues}
              />
            </Box>
          </Grid>
        </>
      )}

      {/* Navigation */}
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button variant="outlined" startIcon={<NavigateBefore />} onClick={goPrev}>
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={<NavigateNext />}
            onClick={handleNext}
            disabled={!formState.template_name}
          >
            Next: Schedule
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};
